import supabase from "./supabase.js";

const getEventValue = (event, ...keys) =>
    keys.map(key => event?.[key]).find(value => value != null && (typeof value !== "string" || value.trim()));

const needsResearchEnrichment = (event) =>
    !String(
        getEventValue(
            event,
            "ticketsUrl",
            "TicketsUrl",
            "ticketUrl",
            "TicketUrl",
            "eventUrl",
            "EventUrl"
        ) || ""
    ).trim() ||
    !String(getEventValue(event, "description", "Description") || "").trim();

const getEventIdentity = (event) => {
    const name = String(getEventValue(event, "name", "Name") || "").trim().toLowerCase();
    const venue = String(getEventValue(event, "venue", "Venue") || "").trim().toLowerCase();
    const time = new Date(getEventValue(event, "startDatetime", "StartDateTime")).getTime();
    return name && venue && Number.isFinite(time) ? JSON.stringify([name, venue, time]) : null;
};

const mergeCitations = (...citationLists) => {
    const seen = new Set();

    return citationLists.flat().filter((citation) => {
        const citationData = citation?.url_citation || citation;
        const key = citationData?.url || JSON.stringify(citation);

        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const invokeEventSearch = async (body) => {
    let timer;
    let result;
    try {
        result = await Promise.race([
            supabase.functions.invoke("ai-fetch-events", { body }),
            new Promise((_, reject) => {
                timer = setTimeout(() => reject(new Error(
                    "The AI search did not respond within 150 seconds. Try a shorter date range or fewer events. The server request may still be running."
                )), 150_000);
            }),
        ]);
    } finally {
        clearTimeout(timer);
    }
    const { data, error } = result;

    if (error) {
        console.error("[Edge Function Error]", error);
        let details;
        try {
            details = await error.context?.json();
        } catch { /* The gateway can return a non-JSON error page. */ }
        throw new Error(
            details?.openAiError?.error?.message || details?.error ||
            error.message || "Failed to fetch events from the edge function"
        );
    }

    if (data?.error) {
        console.error("[Edge Function Response Error]", data);
        throw new Error(
            data.error ||
            "Edge function returned an error"
        );
    }

    return data || {};
};

/**
 * Searches for queer events using the ai-fetch-events edge function.
 *
 * @param {Object} params
 * @param {string} params.country - Country name (e.g., "Australia")
 * @param {string} [params.countryCode] - Country code (e.g., "AU")
 * @param {string} params.state - State or region name
 * @param {string} params.city - City name
 * @param {string} [params.prompt] - Custom search prompt
 * @returns {Promise<{events: Array, citations: Array, raw: string}>}
 */
export async function searchEventsWithAI({ country, countryCode, state, city, prompt, onProgress = () => {} }) {
    if (!country?.trim()) {
        throw new Error("Country is required");
    }

    if (!city?.trim()) {
        throw new Error("City is required");
    }

    const requestLocation = {
        country: country.trim(),
        countryCode: countryCode?.trim(),
        state: state?.trim(),
        city: city.trim(),
    };
    onProgress("Searching the web and verifying event pages�");
    const data = await invokeEventSearch({
        ...requestLocation,
        prompt: prompt?.trim(),
    });
    if (!Array.isArray(data.events) || data.events.some(event => !event || typeof event !== "object" || Array.isArray(event))) {
        throw new Error("Event search returned an invalid events array.");
    }
    const events = data.events;
    const incompleteEvents = events.filter(needsResearchEnrichment);

    if (incompleteEvents.length === 0) {
        return {
            events,
            citations: data.citations || [],
            raw: data.raw || "",
        };
    }

    const researchTargets = incompleteEvents.map((event) => ({
        name: getEventValue(event, "name", "Name") || "",
        venue: getEventValue(event, "venue", "Venue") || "",
        startDatetime:
            getEventValue(event, "startDatetime", "StartDateTime") || "",
    }));

    onProgress(`Found ${events.length} events. Researching missing details for ${incompleteEvents.length}�`);
    try {
        const enrichment = await invokeEventSearch({
            ...requestLocation,
            prompt: `Research only the following already-discovered events:\n${JSON.stringify(researchTargets)}\n\nReturn exactly one JSON object for each listed event. Preserve name, venue, and startDatetime exactly so each occurrence can be identified. Each object must include name, venue, startDatetime, ticketsUrl, and description. ticketsUrl must be a real, direct, verified official event or ticket page—not a homepage, search result, social profile, or invented URL. Read that page and write an original 1–3 sentence description that is fun, cheeky, welcoming, useful, and factual. Paraphrase rather than copying promotional text. Return plain description text without citations, URLs, or Markdown. Exclude unsupported claims. Return only a JSON array.`,
        });
        const enrichedByIdentity = new Map();
        for (const event of enrichment.events || []) {
            const key = getEventIdentity(event);
            if (!key) continue;
            // Ambiguous matches must remain unfilled for manual review.
            enrichedByIdentity.set(key, enrichedByIdentity.has(key) ? null : event);
        }
        const mergedEvents = events.map((event) => {
            if (!needsResearchEnrichment(event)) return event;

            const enrichedEvent = enrichedByIdentity.get(getEventIdentity(event));

            if (!enrichedEvent) return event;

            return {
                ...event,
                ticketsUrl:
                    getEventValue(
                        event,
                        "ticketsUrl",
                        "TicketsUrl",
                        "ticketUrl",
                        "TicketUrl",
                        "eventUrl",
                        "EventUrl"
                    ) ||
                    getEventValue(
                        enrichedEvent,
                        "ticketsUrl",
                        "TicketsUrl",
                        "ticketUrl",
                        "TicketUrl",
                        "eventUrl",
                        "EventUrl"
                    ) ||
                    "",
                description:
                    getEventValue(event, "description", "Description") ||
                    getEventValue(
                        enrichedEvent,
                        "description",
                        "Description"
                    ) ||
                    "",
            };
        });

        return {
            events: mergedEvents,
            citations: mergeCitations(
                data.citations || [],
                enrichment.citations || []
            ),
            raw: data.raw || "",
        };
    } catch (error) {
        console.warn("[Event Enrichment Warning]", error);
    }

    return {
        events,
        citations: data.citations || [],
        raw: data.raw || "",
    };
}
